import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { addTrustLedger, createParticipationFunding, createServicingParticipation } from 'services'
import { filterParticipants } from 'services/apis/participants'
import { Modal } from 'stories/components'
import { getPrice2decimal } from 'utils'
import { RenderInput } from 'utils/RenderInput'

import type { ILenderParticipation, IParticipant } from './types'

interface ParticipantOption {
  id: number
  fullName: string
  email: string
}

interface Allocation {
  key: string
  name: string
  amount: string
  // Set when the row is a participant already on this loan — the allocation is added to their
  // current balance rather than creating a new participant record.
  existingId?: number
  existingAmount: number
  existingInfo?: IParticipant
  // The loan's lender, who holds the part of the loan that was never sold down but has no
  // participation of their own yet. Allocating to them writes one down for their whole holding.
  isLender?: boolean
  // Global participant (participant-user) id for a newly added participant — stored on the record
  // so payment distributions can be logged to the right participant's history reliably.
  participantUserId?: string
}

const parseAmount = (v: any) => Number(String(v ?? '').replace(/[$,\s]/g, '')) || 0

// Group the integer part with commas while typing, preserving a trailing decimal. A leading minus is
// kept so an allocation can be negative - the assignor side of an ownership assignment.
const formatAmountInput = (v: any) => {
  const raw = String(v ?? '')
  const negative = raw.trimStart().startsWith('-')
  const cleaned = raw.replace(/[^0-9.]/g, '')
  const [intPart, ...rest] = cleaned.split('.')
  const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  const body = rest.length ? `${grouped}.${rest.join('')}` : grouped
  return negative ? `-${body}` : body
}

/**
 * Wizard to add a loan participation:
 *   Step 1 — participation date, total amount, and whether to record it in the trust ledger.
 *   Step 2 — allocate that total across the loan's existing participants (and any newly added
 *            from the investor/servicer directory); the process can only complete once the entire
 *            amount is allocated.
 */
export const AddParticipationWizard = ({
  existing,
  lender,
  interestRate,
  loading,
  onClose,
  onCreated,
}: {
  existing: IParticipant[]
  lender: ILenderParticipation | null
  interestRate: string
  loading: boolean
  onClose: () => void
  onCreated: () => void
}) => {
  const [step, setStep] = useState<1 | 2>(1)
  const [date, setDate] = useState('')
  const [amount, setAmount] = useState('')
  const [recordInTrustLedger, setRecordInTrustLedger] = useState(false)

  const [allocations, setAllocations] = useState<Allocation[]>([])
  const [participants, setParticipants] = useState<ParticipantOption[]>([])
  const [addSelection, setAddSelection] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const total = parseAmount(amount)

  // Seed the allocation list from the participants already on this loan (unique by name), plus the
  // lender when they still hold part of the loan without a participation of their own. Seeding the
  // lender with what they already hold is what makes an allocation to them add to that balance
  // instead of replacing it.
  useEffect(() => {
    const seen = new Set<string>()
    const rows: Allocation[] = []
    if (lender?.participantName) {
      seen.add(lender.participantName.trim())
      rows.push({
        key: 'lender',
        name: lender.participantName,
        amount: '',
        existingAmount: lender.participantAmount,
        participantUserId: lender.participantUserId ? String(lender.participantUserId) : '',
        isLender: true,
      })
    }
    ;(existing || []).forEach((p) => {
      const name = (p.participantName || '').trim()
      if (name && !seen.has(name)) {
        seen.add(name)
        rows.push({
          key: `existing-${name}`,
          name,
          amount: '',
          existingId: p.id,
          existingAmount: parseAmount(p.participantAmount),
          existingInfo: p,
          // Carried through so the lender's own row still says so once it has been written down:
          // it reaches this list as an ordinary participation, and "currently holds" on the row
          // that holds the unsold balance means something different from the rest.
          isLender: p.isLender,
        })
      }
    })
    setAllocations(rows)
  }, [existing, lender])

  useEffect(() => {
    // The company investor directory (Manage Investors). Loan investors are chosen from here
    // so payment distributions can be attributed to the participant by id.
    filterParticipants({ query: '', count: 1000, skip: 0, orderBy: 'fullName', orderDir: 1 })
      .then((res: any) => setParticipants(Array.isArray(res?.data) ? res.data : []))
      .catch(() => {})
  }, [])

  const allocated = Number(allocations.reduce((sum, a) => sum + parseAmount(a.amount), 0).toFixed(2))
  const remaining = Number((total - allocated).toFixed(2))
  const overAllocated = remaining < -0.005
  const hasNonZeroAllocation = allocations.some((a) => Math.abs(parseAmount(a.amount)) >= 0.005)
  // A zero-dollar event whose rows net to zero (a negative offset by an equal positive) is an
  // assignment: ownership moving between participants with no new cash into the loan.
  const isAssignment = total < 0.005 && allocations.some((a) => parseAmount(a.amount) < 0)
  // Complete once the rows balance to the total (which is 0 for an assignment) and at least one row
  // actually moves money or ownership.
  const fullyAllocated = Math.abs(remaining) < 0.005 && hasNonZeroAllocation

  const participantOptions = useMemo(() => {
    const rec: Record<string, string> = {}
    participants.forEach((pt) => {
      // Skip participants already present in the allocation list.
      if (!allocations.some((a) => a.name === pt.fullName)) {
        rec[String(pt.id)] = pt.email ? `${pt.fullName} (${pt.email})` : pt.fullName
      }
    })
    return rec
  }, [participants, allocations])

  const setRowAmount = (key: string, value: string) =>
    setAllocations((rows) => rows.map((r) => (r.key === key ? { ...r, amount: value } : r)))

  const fillRemaining = (key: string) =>
    setAllocations((rows) =>
      rows.map((r) =>
        r.key === key ? { ...r, amount: formatAmountInput(String(parseAmount(r.amount) + Math.max(remaining, 0))) } : r,
      ),
    )

  const removeRow = (key: string) => setAllocations((rows) => rows.filter((r) => r.key !== key))

  const addParticipant = () => {
    if (!addSelection) return
    const pt = participants.find((i) => String(i.id) === addSelection)
    const name = pt ? pt.fullName : addSelection
    if (allocations.some((a) => a.name === name)) {
      toast('That investor is already in the list.', { type: 'warning' })
      return
    }
    setAllocations((rows) => [
      ...rows,
      { key: `new-${addSelection}-${rows.length}`, name, amount: '', existingAmount: 0, participantUserId: addSelection },
    ])
    setAddSelection('')
  }

  // Allow a $0 total so a net-zero assignment can proceed; over-allocation is caught in step 2.
  const canProceed = !!date && total >= 0

  const onComplete = async () => {
    if (!fullyAllocated) return
    const rate = String(Number(interestRate) || 0)
    // Keep every non-zero row - negatives included, so the assignor side of an assignment is sent.
    const rows = allocations.filter((a) => Math.abs(parseAmount(a.amount)) >= 0.005)
    setSubmitting(true)
    try {
      // The backend is the single source of truth for ownership basis: recording the funding event
      // applies each allocation to the participant's `participantAmount` (and, on an assignment,
      // carries the paid-down principal with it). So the client only makes sure each participant has
      // a participation row, then records the event. A new participant is created at a zero basis -
      // the funding allocation brings them to their amount - and never has its balance set here.
      const payload: Array<{ participantId?: number; participantName: string; amount: number }> = []
      for (const row of rows) {
        let participationId = row.existingId
        if (!participationId) {
          // The lender is the exception to the zero basis: they already hold the part of the loan
          // that was never sold down, so their participation is written down for that balance and
          // the funding allocation is added on top of it. The flag is what keeps the balance they
          // already held from being counted into the loan a second time.
          const created: any = await createServicingParticipation({
            participantName: row.name,
            participantUserId: row.participantUserId ?? '',
            participantAmount: row.isLender ? String(row.existingAmount) : '0',
            ...(row.isLender ? { isLender: true } : {}),
            participantRepName: '',
            participantRepTitle: '',
            participantAddress: '',
            participantServicingFee: '0',
            participantServicingFeePercent: '0',
            principalRate: rate,
            principalBuyoutRate: rate,
            participantInterestRate: rate,
            participantExcessReturnPercent: '0',
            participantExtensionPercent: '0',
            participantOriginationFeePercent: '0',
            participationDate: date,
          })
          participationId = created?.id
        }
        payload.push({ participantId: participationId, participantName: row.name, amount: parseAmount(row.amount) })
      }

      // A net-zero assignment moves no cash, so only a real positive funding hits the trust ledger.
      if (recordInTrustLedger && total > 0.005) {
        await addTrustLedger({
          type: 'Deposit',
          date,
          reference: 'Participation',
          fromWhom: rows.map((r) => r.name).join(', ').slice(0, 250) || 'Investors',
          deposit: total,
          subType: 'participation',
          notes: 'Loan investor funding',
          files: [],
        })
      }

      // Record the funding event - this is what applies basis and shows in the funding history.
      const res: any = await createParticipationFunding({
        date,
        amount: String(total),
        recordedInTrustLedger: recordInTrustLedger && total > 0.005,
        allocations: payload,
      })
      if (res?.success === 0) {
        toast(res?.message || 'Could not record the investment.', { type: 'error' })
        return
      }

      toast(
        isAssignment
          ? `Recorded an assignment across ${rows.length} participant(s).`
          : `Added participation of $${getPrice2decimal(total)} across ${rows.length} participant(s).`,
        { type: 'success' },
      )
      onCreated()
    } catch (error) {
      console.log('Failed to save investment', error)
      toast('Failed to save the investment.', { type: 'error' })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal
      title={`Add Participation — Step ${step} of 2`}
      titleOkay={step === 1 ? 'Next' : 'Complete'}
      titleCancel="Cancel"
      loading={loading || submitting}
      disabled={step === 1 ? !canProceed : !fullyAllocated}
      onClose={() => onClose()}
      onOk={() => (step === 1 ? setStep(2) : onComplete())}
      childLevel={2}
      isOpen
    >
      <div className="text-gray-900 w-full max-w-2xl">
        {step === 1 && (
          <div className="grid grid-cols-1 gap-4">
            <RenderInput
              input={{ inputType: 'text', type: 'date', title: 'Investment Date', value: date, required: true }}
              Key="date"
              onChange={(_k: string, v: any) => setDate(v)}
            />
            <RenderInput
              input={{
                inputType: 'text',
                type: 'thousandSep',
                prefix: '$',
                title: 'Investment Amount',
                value: amount,
                required: true,
              }}
              Key="amount"
              onChange={(_k: string, v: any) => setAmount(v)}
            />
            {total > 0.005 ? (
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={recordInTrustLedger}
                  onChange={(e) => setRecordInTrustLedger(e.target.checked)}
                />
                Record this participation in the trust ledger
              </label>
            ) : (
              <p className="rounded-md bg-slate-50 px-3 py-2 text-[13px] text-gray-600">
                A $0 participation is treated as an <span className="font-medium">assignment</span> - on the next
                step, enter a negative amount for the participant giving up ownership and an equal positive for the
                one receiving it. No cash moves and nothing is posted to the trust ledger.
              </p>
            )}
          </div>
        )}

        {step === 2 && (
          <div>
            <button
              type="button"
              onClick={() => setStep(1)}
              className="mb-2 text-[13px] text-shade-blue hover:underline"
            >
              ← Back to details
            </button>
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-md bg-slate-50 px-3 py-2 text-sm">
              <span>
                Total: <span className="font-semibold">${getPrice2decimal(total)}</span>
              </span>
              <span>
                Allocated: <span className="font-semibold">${getPrice2decimal(allocated)}</span>
              </span>
              <span className={overAllocated ? 'text-red-600' : fullyAllocated ? 'text-green-700' : ''}>
                Remaining: <span className="font-semibold">${getPrice2decimal(remaining)}</span>
              </span>
            </div>

            <div className="overflow-hidden rounded-md border border-slate-200">
              <table className="w-full text-sm">
                <thead className="bg-gray-100 text-xs uppercase text-gray-600">
                  <tr>
                    <th className="px-3 py-2 text-left">Investor</th>
                    <th className="px-3 py-2 text-right">Allocation</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {allocations.length === 0 && (
                    <tr>
                      <td colSpan={3} className="px-3 py-3 text-center text-gray-400">
                        No participants yet — add one below.
                      </td>
                    </tr>
                  )}
                  {allocations.map((row) => (
                    <tr key={row.key} className="border-t border-slate-100">
                      <td className="px-3 py-1.5">
                        {row.name}
                        {row.existingAmount > 0 && (
                          <div className="text-[11px] text-gray-500">
                            {row.isLender ? 'Lender, currently holds' : 'Currently holds'} $
                            {getPrice2decimal(row.existingAmount)}
                            {parseAmount(row.amount) > 0 && (
                              <span className="text-gray-600">
                                {' '}
                                &rarr; ${getPrice2decimal(row.existingAmount + parseAmount(row.amount))}
                              </span>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-1.5">
                        <div className="relative ml-auto w-40">
                          <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-2 text-sm text-gray-500">
                            $
                          </span>
                          <input
                            type="text"
                            inputMode="decimal"
                            value={row.amount}
                            onChange={(e) => setRowAmount(row.key, formatAmountInput(e.target.value))}
                            className="w-full rounded-md border border-slate-300 bg-white py-1.5 pl-5 pr-2 text-right text-sm focus:border-shade-blue focus:outline-none focus:ring-2 focus:ring-shade-blue/20"
                          />
                        </div>
                      </td>
                      <td className="px-2 py-1.5">
                        <div className="flex items-center justify-end gap-2">
                          {remaining > 0.005 && (
                            <button
                              type="button"
                              className="text-[12px] text-shade-blue hover:underline"
                              onClick={() => fillRemaining(row.key)}
                            >
                              + remaining
                            </button>
                          )}
                          <button
                            type="button"
                            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-red-600"
                            onClick={() => removeRow(row.key)}
                            aria-label={`Remove ${row.name}`}
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-3 flex items-end gap-2">
              <div className="min-w-0 flex-1">
                <label className="mb-1 block text-[12px] text-gray-600">Add an investor</label>
                {/* Native select renders options in the browser top layer, so it is never clipped
                    by the modal's overflow container (unlike an absolutely-positioned dropdown). */}
                <select
                  value={addSelection}
                  onChange={(e) => setAddSelection(e.target.value)}
                  className="w-full rounded-md border border-slate-300 bg-white px-2 py-2 text-sm focus:border-shade-blue focus:outline-none focus:ring-2 focus:ring-shade-blue/20"
                >
                  <option value="">- Select an investor -</option>
                  {Object.entries(participantOptions).map(([id, label]) => (
                    <option key={id} value={id}>
                      {label as string}
                    </option>
                  ))}
                </select>
              </div>
              <button
                type="button"
                disabled={!addSelection}
                onClick={addParticipant}
                className="flex items-center gap-1 rounded bg-shade-blue px-3 py-2 text-[13px] font-medium text-white disabled:opacity-40"
              >
                <PlusIcon className="h-4 w-4" /> Add
              </button>
            </div>

            {overAllocated && (
              <p className="mt-2 text-[13px] text-red-600">
                {total < 0.005
                  ? `Assignments must net to zero - the allocations are off by $${getPrice2decimal(Math.abs(remaining))}.`
                  : `Allocations exceed the participation amount by $${getPrice2decimal(Math.abs(remaining))}.`}
              </p>
            )}
            {!fullyAllocated && !overAllocated && (
              <p className="mt-2 text-[13px] text-gray-500">
                {total < 0.005
                  ? 'Enter a negative amount for the investor giving up ownership and an equal positive for the one receiving it.'
                  : `Allocate the full amount to complete - $${getPrice2decimal(remaining)} remaining.`}
              </p>
            )}
          </div>
        )}
      </div>
    </Modal>
  )
}
